import './MetricPickerChart.scss';
// For the `chart-tooltip` skin on the disabled KPI picker's explainer below — the same borderless,
// 12px surface the chart context-info and truncated-title popovers use.
import './chart-tooltip.scss';
import cn from 'classnames';
import { titleize } from 'inflected';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import {
  ChartConfig,
  QueryResponse,
  DimensionId,
  getFieldMeta,
  KpiId,
  parseOutputFieldRef,
  resolveBreakdownRef,
  resolveMetricOptions,
  SeriesChannel
} from '@blisspointmedia/bpm-types/dist/dashboarding';
import { Dropdown, MenuItem } from '@Components/Dropdown';
import { Placement, Size, StretchMode } from '@Components/designConstants';
import { Spinner } from '@Components/Spinner';
import { breakdownOptionsFor, metricIgnoresKpi } from './chart-renderer-utils';
import { ChartRenderer } from './ChartRenderer';
import { StatRenderer } from './StatRenderer';
import { useFieldMetaCtx } from './useFieldMetaCtx';

// A per-chart KPI dropdown, owned by the parent (which holds the override + refetch). Provided
// only when the chart uses a KPI; MetricPickerChart stays pure and just renders it.
interface KpiPicker {
  options: MenuItem<KpiId>[];
  value: KpiId;
  onChange: (kpiId: KpiId) => void;
}

interface MetricPickerChartProps {
  config: ChartConfig;
  data: QueryResponse;
  className?: string;
  kpiPicker?: KpiPicker;
  // The metric and breakdown picks are LOCAL state by default — a chart standing on its own owns its
  // own picks. Passing either value (including `null` for "nothing picked yet") makes that picker
  // controlled instead, so a surface that has to survive the pick — a dashboard card handing its
  // view to the focused view on expand, the focused view mirroring it into the URL — can hold it.
  metric?: string | null;
  onMetricChange?: (colId: string) => void;
  breakdown?: DimensionId | null;
  onBreakdownChange?: (dimId: DimensionId) => void;
  // Forwarded to the underlying ChartRenderer; dashboard cards pass false so the card header owns
  // the title. Defaults to true so standalone uses keep the in-body title.
  showTitle?: boolean;
  // A refetch is in flight and `data` is the previous result: dims the chart (not the pickers) so a
  // change reads as registered rather than ignored.
  isRefetching?: boolean;
}

// "campaign_name" -> "Campaign Name". Still the right tool for DIMENSIONS: getFieldMeta treats a
// dimension as a display-ready passthrough and labels it with its bare id (custom segments aren't in
// the registry, so there's nothing to look up), which would read worse than this. Metrics go through
// getFieldMeta instead — see metricLabel.
const prettyIdLabel = (id: string): string => titleize(id);

// The underlying metric of an output ref, ignoring its query scope: "q0.revenue" and
// "q1.revenue" both map to "revenue". This is what groups a multi-query chart's series so
// one "Revenue" pick plots BOTH the q0 and q1 lines (e.g. platform vs. source-of-truth).
const metricColId = (ref: string): string => parseOutputFieldRef(ref).colId;

// Wraps the pure ChartRenderer with a metric picker. Its y-series are grouped by underlying metric
// (the colId, ignoring query scope), so the dropdown toggles the METRIC and each selection plots
// every series of that metric — e.g. a platform line and a source-of-truth line for "Revenue".
// Swapping is a pure presentation change (re-point encoding.y), re-rendered with NO refetch. The
// first control of the "view controller", shared by the Dashboard Agent canvas and dashboard chart
// cards so the picker travels with the chart. Tables/stat have no y to swap, so the picker hides.
export const MetricPickerChart = ({
  config,
  data,
  className,
  kpiPicker,
  metric,
  onMetricChange,
  breakdown: controlledBreakdown,
  onBreakdownChange,
  showTitle = true,
  isRefetching = false
}: MetricPickerChartProps) => {
  const [localColId, setLocalColId] = useState<string | null>(null);
  const [localBreakdown, setLocalBreakdown] = useState<DimensionId | null>(null);

  // Controlled when the prop is supplied at all: `null` is a real value ("nothing picked yet"), so
  // only `undefined` means "this picker keeps its own state".
  const isMetricControlled = metric !== undefined;
  const isBreakdownControlled = controlledBreakdown !== undefined;
  const pickedColId = isMetricControlled ? metric : localColId;
  const pickedBreakdown = isBreakdownControlled ? controlledBreakdown : localBreakdown;

  const pickMetric = (colId: string) => {
    if (!isMetricControlled) {
      setLocalColId(colId);
    }
    onMetricChange?.(colId);
  };

  // This component renders ChartRenderer as a child, so it can't receive the ctx from it — it builds
  // its own. The underlying react-query cache is keyed by (company, channel), so the two hooks share
  // one fetch rather than doubling it.
  const ctx = useFieldMetaCtx(config);

  // Reset selections when the chart itself changes, so a stale pick can't leak across charts. Only
  // the local ones: a controlled pick is the owner's to reset (both owners today key their state by
  // chart id, so nothing leaks there either).
  useEffect(() => {
    setLocalColId(null);
    setLocalBreakdown(null);
  }, [config.id]);

  // y-series grouped by underlying metric, preserving each series' authored metadata (label, color,
  // axis side, mark). Insertion order follows first appearance in encoding.y.
  const metricGroups = useMemo<Map<string, SeriesChannel[]>>(() => {
    const groups = new Map<string, SeriesChannel[]>();
    if (config.type === 'table' || config.type === 'stat') return groups;
    for (const series of config.encoding.y) {
      const colId = metricColId(series.ref);
      const existing = groups.get(colId);
      if (existing) {
        existing.push(series);
      } else {
        groups.set(colId, [series]);
      }
    }
    return groups;
  }, [config]);

  // Label a metric through getFieldMeta, so a custom metric shows its display name ("30d AOV (First
  // Touch)", not "30d_aov_ft") and a registry metric its authored pretty name ("ROAS", not "Roas").
  // Prefer a plotted series' ref over the bare colId: a custom metric's display metadata is scoped per
  // channel, and only the ref carries the query scope getFieldMeta resolves that channel from.
  // The bare-colId fallback is a defense against an UNVALIDATED config, not a normal path —
  // CONTROL_OPTION_NOT_IN_ENCODING rejects a declared metric option that isn't in encoding.y, so for
  // any config that passed validation the lookup always hits. It degrades to the registry label.
  const metricLabel = (colId: string): string =>
    getFieldMeta(metricGroups.get(colId)?.[0]?.ref ?? colId, { ctx }).label;

  // The metric picker is OPT-IN: it appears only when the chart declares a `metric` control, and then
  // it toggles a single metric at a time. With NO metric control, a multi-metric chart draws ALL its
  // series (e.g. spend + ROAS as two lines / dual-axis) instead of collapsing to one line + a picker.
  const { options: declaredOptions, activeDefault } = resolveMetricOptions(config);
  const hasMetricControl = declaredOptions.length > 0;
  const metricOptions: MenuItem<string>[] = hasMetricControl
    ? declaredOptions.map(colId => ({ value: colId, label: metricLabel(colId) }))
    : [];

  // A CONTROLLED pick can arrive from a URL someone hand-edited, so it's the one pick that isn't
  // guaranteed to be one of this chart's options — ignore a metric it can't plot and fall through to
  // the declared default, rather than narrowing encoding.y to nothing.
  const validPick = pickedColId && declaredOptions.includes(pickedColId) ? pickedColId : null;
  const activeColId = hasMetricControl
    ? (validPick ?? activeDefault ?? metricOptions[0]?.value ?? null)
    : null;

  // Declaration-first breakdown (color-split) picker: a declared `breakdown` control lists the
  // selectable dimensions and its default. The live selection lives in React (never written back to
  // config.controls); the owner refetches grouped by it (useChartData's `breakdown` override), and it
  // drives ChartRenderer's pivot via breakdownRef.
  const breakdown = resolveBreakdownRef(config);
  const breakdownOptions: MenuItem<DimensionId>[] = breakdownOptionsFor(config).map(dimId => ({
    value: dimId,
    label: prettyIdLabel(dimId)
  }));
  const activeBreakdown = pickedBreakdown ?? breakdown?.ref ?? null;

  const onSelectBreakdown = (dimId: DimensionId) => {
    if (!isBreakdownControlled) {
      setLocalBreakdown(dimId);
    }
    onBreakdownChange?.(dimId);
  };

  // The config actually rendered. With a metric control, encoding.y is narrowed to the selected
  // metric's series — one line per query/source — keeping authored labels/colors so the legend reads
  // e.g. "Platform Revenue". With no control, the config renders as-is so every authored metric draws.
  const viewConfig = useMemo<ChartConfig>(() => {
    if (config.type === 'table' || config.type === 'stat' || !hasMetricControl || !activeColId) {
      return config;
    }
    const series = metricGroups.get(activeColId) ?? config.encoding.y;
    return { ...config, encoding: { ...config.encoding, y: series } };
  }, [config, hasMetricControl, activeColId, metricGroups]);

  const showMetricPicker = hasMetricControl && metricOptions.length > 1 && activeColId;
  const showBreakdownPicker = breakdownOptions.length > 1 && activeBreakdown;

  // A delivery-only metric doesn't read a KPI, so switching one would leave the chart identical —
  // disable the control instead of letting it look live and do nothing.
  const kpiIsInert = activeColId !== null && metricIgnoresKpi(activeColId);

  const hasControls = Boolean(showMetricPicker || showBreakdownPicker || kpiPicker);
  // menuStrategy='fixed' on every picker: .chart-container and .dashboard-card clip their overflow,
  // so an absolute menu is cut off at the card edge.
  const controls = hasControls && (
    <>
      {showMetricPicker && (
        <Dropdown
          overlayPlacement={Placement.RIGHT}
          size={Size.SM}
          label='Metric'
          options={metricOptions}
          value={activeColId}
          onChange={pickMetric}
          stretch={StretchMode.FIT}
          menuStrategy='fixed'
        />
      )}
      {showBreakdownPicker && (
        <Dropdown
          overlayPlacement={Placement.RIGHT}
          size={Size.SM}
          label='Breakdown'
          options={breakdownOptions}
          value={activeBreakdown}
          onChange={onSelectBreakdown}
          stretch={StretchMode.FIT}
          menuStrategy='fixed'
        />
      )}
      {kpiPicker && (
        <InertKpiExplainer
          active={kpiIsInert}
          metricLabel={activeColId ? metricLabel(activeColId) : ''}
        >
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.SM}
            label='KPI'
            options={kpiPicker.options}
            value={kpiPicker.value}
            onChange={kpiPicker.onChange}
            stretch={StretchMode.FIT}
            menuStrategy='fixed'
            disabled={kpiIsInert}
          />
        </InertKpiExplainer>
      )}
    </>
  );
  // A stat draws the pickers on its value row instead of a row of their own.
  const isStat = config.type === 'stat';

  return (
    <div className={cn('metric-picker-chart', className)}>
      {controls && !isStat && <div className='metric-picker-chart-controls'>{controls}</div>}
      <div className={cn('metric-picker-chart-body', { refetching: isRefetching })}>
        {isStat ? (
          <StatRenderer
            config={viewConfig}
            data={data}
            showTitle={showTitle}
            ctx={ctx}
            controls={controls}
          />
        ) : (
          <ChartRenderer
            config={viewConfig}
            data={data}
            showTitle={showTitle}
            breakdownRef={pickedBreakdown ?? undefined}
          />
        )}
        {isRefetching && (
          <div className='metric-picker-chart-refetch-spinner'>
            <Spinner size={24} />
          </div>
        )}
      </div>
    </div>
  );
};

interface InertKpiExplainerProps {
  active: boolean;
  metricLabel: string;
  children: ReactElement;
}

// Says WHY the KPI picker is greyed out. The explanation anchors to a wrapping span rather than the
// control itself because a disabled button fires no hover events, so an overlay on the Dropdown
// would never open.
//
// A Popover on the `info-tooltip-popover chart-tooltip` surface rather than Bootstrap's `Tooltip`,
// which is the dark, large-text default. Every other explanatory hover on a chart — the
// context-info popover, the truncated-title popover — uses this borderless 12px surface, so reusing
// the class keeps them one thing instead of a second look that has to be maintained in parallel.
const InertKpiExplainer = ({
  active,
  metricLabel,
  children
}: InertKpiExplainerProps): ReactElement => {
  if (!active) return children;
  return (
    <OverlayTrigger
      trigger={['hover', 'focus']}
      placement={Placement.BOTTOM}
      overlay={
        <Popover className='info-tooltip-popover chart-tooltip'>
          <Popover.Body>
            {`${metricLabel} is a delivery metric — it doesn't depend on a KPI.`}
          </Popover.Body>
        </Popover>
      }
    >
      <span className='kpi-picker-anchor'>{children}</span>
    </OverlayTrigger>
  );
};
